import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Key, 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  Eye, 
  EyeOff,
  Plus,
  Settings,
  Trash2,
  ArrowLeft,
  Bot,
  Brain,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIProviderConfig = ({ onBack }) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [showApiKey, setShowApiKey] = useState({});
  const [formData, setFormData] = useState({
    provider: 'openai',
    api_key: '',
    organization_id: ''
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: providers = [] } = useQuery({
    queryKey: ['ai-providers'],
    queryFn: async () => {
      const response = await axios.get(`${API}/ai-providers`);
      return response.data;
    }
  });

  // Mutations
  const createProviderMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/ai-providers`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['ai-providers']);
      setShowCreateDialog(false);
      setFormData({ provider: 'openai', api_key: '', organization_id: '' });
      
      if (data.validation_result?.valid) {
        toast.success(`${data.provider} configurado exitosamente`);
      } else {
        toast.error(`Error: ${data.validation_result?.message}`);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al configurar proveedor');
    }
  });

  const validateKeyMutation = useMutation({
    mutationFn: async ({ provider, api_key, organization_id }) => {
      const response = await axios.post(`${API}/ai-providers/${provider}/validate`, {
        api_key,
        organization_id
      });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.valid) {
        toast.success('API Key válida ✓');
      } else {
        toast.error(`Key inválida: ${data.message}`);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al validar key');
    }
  });

  const deleteProviderMutation = useMutation({
    mutationFn: async (providerId) => {
      const response = await axios.delete(`${API}/ai-providers/${providerId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ai-providers']);
      toast.success('Configuración eliminada');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  });

  const handleCreateProvider = () => {
    if (!formData.api_key.trim()) {
      toast.error('API Key es requerida');
      return;
    }
    createProviderMutation.mutate(formData);
  };

  const handleValidateKey = () => {
    if (!formData.api_key.trim()) {
      toast.error('Ingrese una API Key para validar');
      return;
    }
    validateKeyMutation.mutate({
      provider: formData.provider,
      api_key: formData.api_key,
      organization_id: formData.organization_id
    });
  };

  const toggleShowApiKey = (providerId) => {
    setShowApiKey(prev => ({
      ...prev,
      [providerId]: !prev[providerId]
    }));
  };

  const getProviderInfo = (provider) => {
    const configs = {
      openai: {
        name: 'OpenAI',
        icon: Bot,
        color: 'text-green-400',
        bgColor: 'bg-green-500/10 border-green-500/30',
        description: 'GPT-5, GPT-4o, GPT-4o-mini, GPT-4.1',
        docs: 'https://platform.openai.com/api-keys'
      },
      anthropic: {
        name: 'Anthropic',
        icon: Brain,
        color: 'text-purple-400',
        bgColor: 'bg-purple-500/10 border-purple-500/30',
        description: 'Claude Opus 4.1, Claude Sonnet 4.5, Claude Haiku 4.5, Claude 3.x',
        docs: 'https://console.anthropic.com/settings/keys'
      }
    };
    return configs[provider] || configs.openai;
  };

  const getStatusBadge = (config) => {
    if (config.validation_status === 'valid') {
      return <Badge className="badge-success">✓ Válida</Badge>;
    } else if (config.validation_status === 'invalid') {
      return <Badge className="badge-error">✗ Inválida</Badge>;
    } else {
      return <Badge className="badge-warning">⏳ Pendiente</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-600" />
          <div>
            <h1 className="text-3xl font-bold text-white">Configuración de Proveedores de IA</h1>
            <p className="text-slate-400">Configure sus API keys de OpenAI y Anthropic</p>
          </div>
        </div>
      </div>

      {/* Current Configurations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {['openai', 'anthropic'].map((providerType) => {
          const config = providers.find(p => p.provider === providerType);
          const info = getProviderInfo(providerType);
          const Icon = info.icon;

          return (
            <Card key={providerType} className={`glass-dark ${info.bgColor}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon className={`w-6 h-6 ${info.color}`} />
                    <div>
                      <CardTitle className="text-white">{info.name}</CardTitle>
                      <p className="text-xs text-slate-400">{info.description}</p>
                    </div>
                  </div>
                  {config && getStatusBadge(config)}
                </div>
              </CardHeader>
              
              <CardContent>
                {config ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        API Key
                      </label>
                      <div className="flex gap-2">
                        <Input
                          type={showApiKey[config.id] ? 'text' : 'password'}
                          value="sk-••••••••••••••••••••••••••••••••••••"
                          readOnly
                          className="form-input flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleShowApiKey(config.id)}
                          className="text-slate-400"
                        >
                          {showApiKey[config.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {config.organization_id && (
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Organization ID
                        </label>
                        <Input
                          value={config.organization_id}
                          readOnly
                          className="form-input"
                        />
                      </div>
                    )}

                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Configurado: {new Date(config.created_at).toLocaleDateString()}</span>
                      {config.last_validated && (
                        <span>Validado: {new Date(config.last_validated).toLocaleDateString()}</span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setSelectedProvider(providerType);
                          setFormData({ ...formData, provider: providerType });
                          setShowCreateDialog(true);
                        }}
                        size="sm"
                        variant="outline"
                        className="flex-1 border-slate-600 text-slate-300"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Actualizar
                      </Button>
                      <Button
                        onClick={() => deleteProviderMutation.mutate(config.id)}
                        size="sm"
                        variant="outline"
                        className="border-red-600 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Key className="w-12 h-12 mx-auto text-slate-500 mb-4" />
                    <p className="text-slate-400 mb-4">No configurado</p>
                    <Button
                      onClick={() => {
                        setSelectedProvider(providerType);
                        setFormData({ ...formData, provider: providerType });
                        setShowCreateDialog(true);
                      }}
                      size="sm"
                      className="btn-gradient"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Configurar {info.name}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Information Panel */}
      <Card className="glass-dark border-blue-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            Información Importante
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-slate-200 mb-2">🔐 Seguridad</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Las API keys se almacenan encriptadas</li>
                <li>• Solo el Super Admin puede configurarlas</li>
                <li>• Se validan automáticamente al guardar</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-slate-200 mb-2">📋 Prioridad de Keys</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• 1º - API keys configuradas aquí</li>
                <li>• Se selecciona automáticamente por módulo</li>
              </ul>
            </div>
          </div>
          
          <div className="pt-4 border-t border-slate-700">
            <h4 className="font-medium text-slate-200 mb-2">📖 Obtener API Keys</h4>
            <div className="flex gap-4">
              <Button
                variant="outline"
                size="sm"
                className="border-green-500/30 text-green-400"
                onClick={() => window.open('https://platform.openai.com/api-keys', '_blank')}
              >
                <Bot className="w-4 h-4 mr-2" />
                OpenAI Platform
                <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-purple-500/30 text-purple-400"
                onClick={() => window.open('https://console.anthropic.com/settings/keys', '_blank')}
              >
                <Brain className="w-4 h-4 mr-2" />
                Anthropic Console
                <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create/Update Provider Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="glass-dark border-blue-500/20 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-400" />
              Configurar {getProviderInfo(formData.provider).name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Proveedor
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={formData.provider === 'openai' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, provider: 'openai' })}
                  className={formData.provider === 'openai' ? 'bg-green-600' : 'border-slate-600'}
                >
                  <Bot className="w-4 h-4 mr-2" />
                  OpenAI
                </Button>
                <Button
                  variant={formData.provider === 'anthropic' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, provider: 'anthropic' })}
                  className={formData.provider === 'anthropic' ? 'bg-purple-600' : 'border-slate-600'}
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Anthropic
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                API Key *
              </label>
              <Input
                type="password"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                className="form-input font-mono"
                placeholder={formData.provider === 'openai' ? 'sk-proj-...' : 'sk-ant-api03-...'}
              />
            </div>

            {formData.provider === 'openai' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Organization ID (opcional)
                </label>
                <Input
                  value={formData.organization_id}
                  onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                  className="form-input"
                  placeholder="org-..."
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleValidateKey}
                variant="outline"
                className="flex-1 border-blue-500/30 text-blue-300"
                disabled={validateKeyMutation.isPending || !formData.api_key}
              >
                {validateKeyMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Validar Key
              </Button>
              <Button
                onClick={handleCreateProvider}
                className="flex-1 btn-gradient"
                disabled={createProviderMutation.isPending || !formData.api_key}
              >
                {createProviderMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                Guardar Configuración
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIProviderConfig;